import { BffStorefrontProvider } from './coutureia-storefront-providers.js';
import { EMPTY_FILTERS } from './coutureia-storefront-contracts.js';
import { facetOptionLabel } from './coutureia-storefront-render.js';

const BFF_BASE_URL = '/apps/coutureia-search/v0.2';
const CACHE_VERSION = 3;
const CACHE_TTL_MS = 10 * 60 * 1000;
const MENU_KEYS = Object.freeze(['brands', 'men', 'women']);
const GROUP_KEYS = Object.freeze(['categories', 'subcategories', 'product_types']);
const CATEGORY_ORDER = Object.freeze(['Clothing', 'Shoes', 'Bags', 'Accessories']);
const DETAIL_LIMIT_DESKTOP = 10;
const DETAIL_LIMIT_MOBILE = 8;

const UI = Object.freeze({
  en: {
    groupLabels: { categories: 'Categories', subcategories: 'Subcategories', product_types: 'Product types' },
    loading: 'Loading menu…',
    unavailable: 'Menu temporarily unavailable.',
    brandsKicker: 'Designers',
    brandsTitle: 'Explore brands',
    brandsSubtitle: 'Discover the designers currently available at Coutureia.',
    brandSearch: 'Search brands',
    popular: 'Popular designers',
    allBrands: 'All designers',
    noBrands: 'No matching designer found.',
    menKicker: 'Men',
    menTitle: 'Shop Men',
    menSubtitle: 'Discover the current men’s selection by category and product type.',
    womenKicker: 'Women',
    womenTitle: 'Shop Women',
    womenSubtitle: 'Discover the current women’s selection by category and product type.',
    shopAllMen: 'Shop all Men',
    shopAllWomen: 'Shop all Women',
    featured: 'Shop by category',
    browse: 'Explore more',
    viewMore: 'View more',
    viewLess: 'View less'
  },
  de: {
    groupLabels: { categories: 'Kategorien', subcategories: 'Unterkategorien', product_types: 'Produkttypen' },
    loading: 'Menü wird geladen…',
    unavailable: 'Menü vorübergehend nicht verfügbar.',
    brandsKicker: 'Designer',
    brandsTitle: 'Marken entdecken',
    brandsSubtitle: 'Entdecken Sie die aktuell bei Coutureia verfügbaren Designer.',
    brandSearch: 'Marken suchen',
    popular: 'Beliebte Designer',
    allBrands: 'Alle Designer',
    noBrands: 'Keine passende Marke gefunden.',
    menKicker: 'Männer',
    menTitle: 'Männer entdecken',
    menSubtitle: 'Entdecken Sie die aktuelle Auswahl nach Kategorie und Produkttyp.',
    womenKicker: 'Frauen',
    womenTitle: 'Frauen entdecken',
    womenSubtitle: 'Entdecken Sie die aktuelle Auswahl nach Kategorie und Produkttyp.',
    shopAllMen: 'Alle Männer-Artikel',
    shopAllWomen: 'Alle Frauen-Artikel',
    featured: 'Nach Kategorie shoppen',
    browse: 'Mehr entdecken',
    viewMore: 'Mehr anzeigen',
    viewLess: 'Weniger anzeigen'
  },
  fr: {
    groupLabels: { categories: 'Catégories', subcategories: 'Sous-catégories', product_types: 'Types de produit' },
    loading: 'Chargement du menu…',
    unavailable: 'Menu temporairement indisponible.',
    brandsKicker: 'Créateurs',
    brandsTitle: 'Découvrir les marques',
    brandsSubtitle: 'Découvrez les créateurs actuellement disponibles chez Coutureia.',
    brandSearch: 'Rechercher une marque',
    popular: 'Créateurs populaires',
    allBrands: 'Tous les créateurs',
    noBrands: 'Aucune marque correspondante.',
    menKicker: 'Hommes',
    menTitle: 'Découvrir Homme',
    menSubtitle: 'Découvrez la sélection actuelle par catégorie et type de produit.',
    womenKicker: 'Femmes',
    womenTitle: 'Découvrir Femme',
    womenSubtitle: 'Découvrez la sélection actuelle par catégorie et type de produit.',
    shopAllMen: 'Voir tout Homme',
    shopAllWomen: 'Voir tout Femme',
    featured: 'Shopping par catégorie',
    browse: 'Explorer davantage',
    viewMore: 'Voir plus',
    viewLess: 'Voir moins'
  }
});

function uiLanguage() {
  const language = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
  return ['de', 'fr'].includes(language) ? language : 'en';
}

function text(language) {
  return UI[language] || UI.en;
}

function bffLanguage(language) {
  return language === 'de' ? 'de' : 'en';
}

function searchPath() {
  const value = document.documentElement.dataset.coutureiaSearchPath || '/search';
  return value.startsWith('/') ? value : '/search';
}

function copyFilters(overrides = {}) {
  const filters = {};
  for (const [key, value] of Object.entries(EMPTY_FILTERS)) {
    filters[key] = Array.isArray(value) ? [...value] : value;
  }
  return Object.assign(filters, overrides);
}

function request(language, overrides = {}) {
  return {
    context: { market_country: 'DEFAULT', language, currency: 'EUR' },
    query: null,
    filters: copyFilters(overrides),
    sort: 'relevance',
    page: { number: 1, size: 20 },
    surface: 'SEARCH_RESULTS'
  };
}

function optionCount(option) {
  const productCount = Number(option?.product_count);
  if (Number.isFinite(productCount)) return Math.max(0, productCount);
  const variantCount = Number(option?.variant_count);
  return Number.isFinite(variantCount) ? Math.max(0, variantCount) : 0;
}

function facetOptions(response, key, language) {
  const facet = Array.isArray(response?.facets) ? response.facets.find(item => item?.key === key) : null;
  if (!facet || !Array.isArray(facet.options)) return [];
  return facet.options
    .filter(option => typeof option?.value === 'string' && option.value.trim() && option.available !== false && optionCount(option) > 0)
    .map(option => ({
      value: option.value,
      label: facetOptionLabel(key, option.value, language),
      count: optionCount(option)
    }));
}

function hierarchyPathOptions(response, key, language) {
  const paths = Array.isArray(response?.hierarchy_paths) ? response.hierarchy_paths : [];
  const grouped = new Map();
  for (const path of paths) {
    if (!path || path.available !== true || optionCount(path) <= 0) continue;
    const category = typeof path.category === 'string' ? path.category.trim() : '';
    const subcategory = typeof path.subcategory === 'string' ? path.subcategory.trim() : '';
    const productType = typeof path.product_type === 'string' ? path.product_type.trim() : '';
    if (!category || !subcategory || !productType) continue;

    let value = '';
    let identity = '';
    let filters = null;
    if (key === 'categories') {
      value = category;
      identity = category;
      filters = { categories: category };
    } else if (key === 'subcategories') {
      value = subcategory;
      identity = `${category}\u0000${subcategory}`;
      filters = { categories: category, subcategories: subcategory };
    } else if (key === 'product_types') {
      value = productType;
      identity = `${category}\u0000${subcategory}\u0000${productType}`;
      filters = { categories: category, subcategories: subcategory, product_types: productType };
    } else {
      continue;
    }

    const current = grouped.get(identity);
    const count = optionCount(path);
    if (current) current.count += count;
    else grouped.set(identity, { value, label: facetOptionLabel(key, value, language), count, filters });
  }
  return [...grouped.values()];
}

function compareOptions(key, a, b, language) {
  if (key === 'categories') {
    const ai = CATEGORY_ORDER.indexOf(a.value);
    const bi = CATEGORY_ORDER.indexOf(b.value);
    if (ai !== bi) {
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    }
  }
  return a.label.localeCompare(b.label, language, { sensitivity: 'base', numeric: true });
}

function normalizeModel(base, male, female, language) {
  const normalizeFacet = (response, key) => facetOptions(response, key, language).sort((a, b) => compareOptions(key, a, b, language));
  const normalizePaths = (response, key) => hierarchyPathOptions(response, key, language).sort((a, b) => compareOptions(key, a, b, language));
  const genderModel = response => Object.fromEntries(GROUP_KEYS.map(key => [key, normalizePaths(response, key)]));
  return {
    brands: normalizeFacet(base, 'brands'),
    men: genderModel(male),
    women: genderModel(female)
  };
}

function cacheKey(language) {
  return `coutureia.system-menu.v${CACHE_VERSION}.${language}`;
}

function readCache(language) {
  try {
    const raw = sessionStorage.getItem(cacheKey(language));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - Number(parsed.savedAt || 0) > CACHE_TTL_MS) return null;
    return parsed.model || null;
  } catch {
    return null;
  }
}

function writeCache(language, model) {
  try {
    sessionStorage.setItem(cacheKey(language), JSON.stringify({ savedAt: Date.now(), model }));
  } catch {
    // Optional cache only.
  }
}

function buildUrl(filterKey, value, gender = null, structuredFilters = null) {
  const params = new URLSearchParams();
  if (gender) params.set('genders', gender);
  if (structuredFilters && typeof structuredFilters === 'object') {
    for (const key of ['categories', 'subcategories', 'product_types']) {
      const filterValue = structuredFilters[key];
      if (typeof filterValue === 'string' && filterValue.trim()) params.set(key, filterValue);
    }
  } else if (filterKey && value != null) {
    params.set(filterKey, value);
  }
  const query = params.toString();
  return query ? `${searchPath()}?${query}` : searchPath();
}

function buildOptionUrl(key, option, gender) {
  if (option?.filters && typeof option.filters === 'object') return buildUrl(null, null, gender, option.filters);
  return buildUrl(key, option?.value, gender);
}

function generated(tag, className = '') {
  const node = document.createElement(tag);
  node.dataset.coutureiaSystemMenuGenerated = 'true';
  if (className) node.className = className;
  return node;
}

function arrow() {
  const span = document.createElement('span');
  span.className = 'coutureia-system-menu__arrow';
  span.setAttribute('aria-hidden', 'true');
  span.textContent = '→';
  return span;
}

function anchorFor(option, href, className = 'coutureia-system-menu__link') {
  const anchor = document.createElement('a');
  anchor.className = className;
  anchor.href = href;
  anchor.dataset.coutureiaSystemMenuLink = 'true';
  anchor.dataset.coutureiaCanonicalValue = option?.value || '';
  const label = document.createElement('span');
  label.className = 'coutureia-system-menu__link-label';
  label.textContent = option?.label || '';
  anchor.append(label);
  return anchor;
}

function linkItem(option, href, { brand = false, extra = false } = {}) {
  const li = document.createElement('li');
  if (brand) {
    li.dataset.coutureiaBrandLabel = option.label.toLocaleLowerCase();
    li.dataset.coutureiaBrandItem = 'true';
  }
  if (extra) li.dataset.coutureiaMenuExtra = 'true';
  const anchor = anchorFor(option, href);
  anchor.append(arrow());
  li.append(anchor);
  return li;
}

function makeIntro({ kicker, title, subtitle, ctaLabel = '', ctaHref = '' }) {
  const header = generated('div', 'coutureia-system-menu__intro');
  const copy = document.createElement('div');
  copy.className = 'coutureia-system-menu__intro-copy';
  const eyebrow = document.createElement('p');
  eyebrow.className = 'coutureia-system-menu__eyebrow';
  eyebrow.textContent = kicker;
  const heading = document.createElement('h2');
  heading.className = 'coutureia-system-menu__title';
  heading.textContent = title;
  const description = document.createElement('p');
  description.className = 'coutureia-system-menu__subtitle';
  description.textContent = subtitle;
  copy.append(eyebrow, heading, description);
  header.append(copy);
  if (ctaLabel && ctaHref) {
    const cta = document.createElement('a');
    cta.className = 'coutureia-system-menu__shop-all';
    cta.href = ctaHref;
    cta.dataset.coutureiaSystemMenuLink = 'true';
    cta.textContent = ctaLabel;
    cta.append(arrow());
    header.append(cta);
  }
  return header;
}

function sectionHeading(label) {
  const heading = document.createElement('p');
  heading.className = 'coutureia-system-menu__section-heading';
  heading.textContent = label;
  return heading;
}

function brandLetter(label) {
  const normalized = String(label || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  const first = normalized.charAt(0).toUpperCase();
  return /^[A-Z]$/.test(first) ? first : '#';
}

function renderBrands(panel, options, language) {
  const t = text(language);
  panel.append(makeIntro({ kicker: t.brandsKicker, title: t.brandsTitle, subtitle: t.brandsSubtitle }));

  const tools = generated('div', 'coutureia-system-menu__brand-tools');
  const searchWrap = document.createElement('label');
  searchWrap.className = 'coutureia-system-menu__brand-search';
  const sr = document.createElement('span');
  sr.className = 'visually-hidden';
  sr.textContent = t.brandSearch;
  const input = document.createElement('input');
  input.type = 'search';
  input.autocomplete = 'off';
  input.placeholder = t.brandSearch;
  input.setAttribute('aria-label', t.brandSearch);
  input.dataset.coutureiaBrandSearch = 'true';
  searchWrap.append(sr, input);
  tools.append(searchWrap);
  panel.append(tools);

  const popular = [...options].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, language, { sensitivity: 'base' })).slice(0, 8);
  if (popular.length) {
    const popularSection = generated('section', 'coutureia-system-menu__popular');
    popularSection.dataset.coutureiaBrandPopular = 'true';
    popularSection.append(sectionHeading(t.popular));
    const popularGrid = document.createElement('div');
    popularGrid.className = 'coutureia-system-menu__popular-grid';
    for (const option of popular) {
      const a = anchorFor(option, buildUrl('brands', option.value), 'coutureia-system-menu__popular-link');
      a.append(arrow());
      popularGrid.append(a);
    }
    popularSection.append(popularGrid);
    panel.append(popularSection);
  }

  const all = generated('section', 'coutureia-system-menu__brand-directory');
  all.append(sectionHeading(t.allBrands));
  const directory = document.createElement('div');
  directory.className = 'coutureia-system-menu__brand-groups';
  directory.dataset.coutureiaBrandDirectory = 'true';

  const groups = new Map();
  for (const option of options) {
    const key = brandLetter(option.label);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(option);
  }
  const orderedKeys = [...groups.keys()].sort((a, b) => a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b));
  for (const key of orderedKeys) {
    const group = document.createElement('section');
    group.className = 'coutureia-system-menu__brand-group';
    group.dataset.coutureiaBrandGroup = 'true';
    const heading = document.createElement('h3');
    heading.className = 'coutureia-system-menu__brand-letter';
    heading.textContent = key;
    const list = document.createElement('ul');
    list.className = 'coutureia-system-menu__links coutureia-system-menu__brand-list';
    for (const option of groups.get(key)) list.append(linkItem(option, buildUrl('brands', option.value), { brand: true }));
    group.append(heading, list);
    directory.append(group);
  }
  all.append(directory);

  const empty = document.createElement('p');
  empty.className = 'coutureia-system-menu__brand-empty';
  empty.dataset.coutureiaBrandEmpty = 'true';
  empty.hidden = true;
  empty.textContent = t.noBrands;
  all.append(empty);
  panel.append(all);

  input.addEventListener('input', () => {
    const query = input.value.trim().toLocaleLowerCase();
    let visible = 0;
    panel.querySelectorAll('[data-coutureia-brand-item="true"]').forEach(item => {
      const match = !query || (item.dataset.coutureiaBrandLabel || '').includes(query);
      item.hidden = !match;
      if (match) visible += 1;
    });
    panel.querySelectorAll('[data-coutureia-brand-group="true"]').forEach(group => {
      group.hidden = !group.querySelector('[data-coutureia-brand-item="true"]:not([hidden])');
    });
    const popularNode = panel.querySelector('[data-coutureia-brand-popular="true"]');
    if (popularNode) popularNode.hidden = Boolean(query);
    empty.hidden = visible !== 0;
  });
}

function syncExpandedMenuViewport(origin) {
  if (!(origin instanceof Element)) return;
  const panel = origin.closest('[data-coutureia-system-menu-panel]');
  if (!(panel instanceof HTMLElement)) return;

  if (panel.dataset.coutureiaSystemMenuSurface === 'mobile') {
    requestAnimationFrame(() => origin.scrollIntoView({ block: 'nearest' }));
    return;
  }

  const submenu = panel.closest('.menu-list__submenu');
  const headerMenu = panel.closest('header-menu');
  if (!(submenu instanceof HTMLElement) || !(headerMenu instanceof HTMLElement)) return;

  requestAnimationFrame(() => {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const submenuTop = Math.max(0, submenu.getBoundingClientRect().top);
    const availableHeight = Math.max(240, Math.floor(viewportHeight - submenuTop - 8));

    submenu.style.maxHeight = `${availableHeight}px`;
    submenu.style.overflowY = submenu.scrollHeight > availableHeight ? 'auto' : 'visible';
    submenu.style.overscrollBehavior = 'contain';
    submenu.style.scrollbarGutter = 'stable';

    const visibleHeight = Math.min(submenu.scrollHeight, availableHeight);
    headerMenu.style.setProperty('--submenu-height', `${visibleHeight}px`);
  });
}

function expandButton(list, hiddenItems, language) {
  if (!hiddenItems.length) return null;
  const t = text(language);
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'coutureia-system-menu__more-button';
  button.setAttribute('aria-expanded', 'false');
  button.textContent = `${t.viewMore} (${hiddenItems.length})`;
  button.addEventListener('click', () => {
    const expanded = button.getAttribute('aria-expanded') === 'true';
    hiddenItems.forEach(item => { item.hidden = expanded; });
    button.setAttribute('aria-expanded', String(!expanded));
    button.textContent = expanded ? `${t.viewMore} (${hiddenItems.length})` : t.viewLess;
    syncExpandedMenuViewport(button);
    if (expanded) list.scrollIntoView({ block: 'nearest' });
  });
  return button;
}

function renderDetailGroup(key, options, gender, language, mobile) {
  if (!options.length) return null;
  const t = text(language);
  const section = document.createElement('section');
  section.className = 'coutureia-system-menu__group';
  section.dataset.coutureiaSystemMenuGroup = key;
  const heading = document.createElement('h3');
  heading.className = 'coutureia-system-menu__group-title';
  heading.textContent = t.groupLabels[key] || UI.en.groupLabels[key];
  const list = document.createElement('ul');
  list.className = 'coutureia-system-menu__links';
  const limit = mobile ? DETAIL_LIMIT_MOBILE : DETAIL_LIMIT_DESKTOP;
  const hiddenItems = [];
  options.forEach((option, index) => {
    const li = linkItem(option, buildOptionUrl(key, option, gender), { extra: index >= limit });
    if (index >= limit) {
      li.hidden = true;
      hiddenItems.push(li);
    }
    list.append(li);
  });
  section.append(heading, list);
  const more = expandButton(list, hiddenItems, language);
  if (more) section.append(more);
  return section;
}

function renderGender(panel, groups, gender, language) {
  const t = text(language);
  const isMen = gender === 'male';
  const mobile = panel.dataset.coutureiaSystemMenuSurface === 'mobile';
  panel.append(makeIntro({
    kicker: isMen ? t.menKicker : t.womenKicker,
    title: isMen ? t.menTitle : t.womenTitle,
    subtitle: isMen ? t.menSubtitle : t.womenSubtitle,
    ctaLabel: isMen ? t.shopAllMen : t.shopAllWomen,
    ctaHref: buildUrl(null, null, gender)
  }));

  const categories = Array.isArray(groups?.categories) ? groups.categories : [];
  const featured = categories.slice(0, 4);
  if (featured.length) {
    const quick = generated('section', 'coutureia-system-menu__quick-section');
    quick.append(sectionHeading(t.featured));
    const quickGrid = document.createElement('div');
    quickGrid.className = 'coutureia-system-menu__quick-grid';
    for (const option of featured) {
      const a = anchorFor(option, buildOptionUrl('categories', option, gender), 'coutureia-system-menu__quick-link');
      a.append(arrow());
      quickGrid.append(a);
    }
    quick.append(quickGrid);
    panel.append(quick);
  }

  const browse = generated('section', 'coutureia-system-menu__browse');
  browse.append(sectionHeading(t.browse));
  const grid = document.createElement('div');
  grid.className = 'coutureia-system-menu__gender-grid';
  const detailGroups = {
    categories: categories.slice(featured.length),
    subcategories: Array.isArray(groups?.subcategories) ? groups.subcategories : [],
    product_types: Array.isArray(groups?.product_types) ? groups.product_types : []
  };
  for (const key of GROUP_KEYS) {
    const section = renderDetailGroup(key, detailGroups[key], gender, language, mobile);
    if (section) grid.append(section);
  }
  if (!grid.children.length && categories.length) {
    const section = renderDetailGroup('categories', categories, gender, language, mobile);
    if (section) grid.append(section);
  }
  browse.append(grid);
  panel.append(browse);
}

function setStatus(panel, state, value) {
  panel.dataset.state = state;
  const status = panel.querySelector('[data-coutureia-system-menu-status]');
  if (status) status.textContent = value;
}

function clearGenerated(panel) {
  panel.querySelectorAll('[data-coutureia-system-menu-generated="true"]').forEach(node => node.remove());
}

function renderAll(model, language) {
  const t = text(language);
  document.querySelectorAll('[data-coutureia-system-menu-panel]').forEach(panel => {
    clearGenerated(panel);
    const key = panel.dataset.coutureiaSystemMenuPanel;
    if (!MENU_KEYS.includes(key)) return;
    if (key === 'brands') renderBrands(panel, model.brands || [], language);
    if (key === 'men') renderGender(panel, model.men, 'male', language);
    if (key === 'women') renderGender(panel, model.women, 'female', language);
    const hasLinks = panel.querySelector('[data-coutureia-system-menu-link="true"]');
    setStatus(panel, hasLinks ? 'ready' : 'unavailable', hasLinks ? '' : t.unavailable);
  });
}

function failClosed(language) {
  const t = text(language);
  document.querySelectorAll('[data-coutureia-system-menu-panel]').forEach(panel => {
    clearGenerated(panel);
    setStatus(panel, 'unavailable', t.unavailable);
  });
}

async function fetchModel(language) {
  const provider = new BffStorefrontProvider(BFF_BASE_URL);
  const lang = bffLanguage(language);
  const controller = new AbortController();
  const [base, male, female] = await Promise.all([
    provider.query(request(lang), controller.signal),
    provider.query(request(lang, { genders: ['male'] }), controller.signal),
    provider.query(request(lang, { genders: ['female'] }), controller.signal)
  ]);
  return normalizeModel(base, male, female, language);
}

function install() {
  if (document.documentElement.dataset.coutureiaSearchFirst !== 'true') return;
  const panels = document.querySelectorAll('[data-coutureia-system-menu-panel]');
  if (!panels.length) return;

  const language = uiLanguage();
  const t = text(language);
  panels.forEach(panel => setStatus(panel, 'loading', t.loading));

  let loadPromise = null;
  const loadOnce = () => {
    if (loadPromise) return loadPromise;
    loadPromise = (async () => {
      const cached = readCache(language);
      if (cached) {
        renderAll(cached, language);
        return cached;
      }
      try {
        const model = await fetchModel(language);
        writeCache(language, model);
        renderAll(model, language);
        return model;
      } catch (error) {
        console.error('[Coutureia system menu] BFF menu load failed closed.', error);
        failClosed(language);
        return null;
      }
    })();
    return loadPromise;
  };

  document.querySelectorAll('[data-coutureia-system-menu-trigger]').forEach(trigger => {
    trigger.addEventListener('pointerenter', loadOnce, { once: true, passive: true });
    trigger.addEventListener('focusin', loadOnce, { once: true });
    trigger.addEventListener('click', loadOnce, { once: true });
  });

  // Search pages already issue a customer-facing BFF results request.
  // Keep on-demand menu loading via pointerenter/focusin/click; avoid
  // prefetching three additional full BFF queries on a cold paid landing.
  const isSearchResultsPage = Boolean(document.querySelector(
    '[data-coutureia-storefront-section][data-coutureia-surface="search-results"]'
  ));
  if (!isSearchResultsPage) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(loadOnce, { timeout: 1800 });
    else window.setTimeout(loadOnce, 900);
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', install, { once: true });
else install();
